import axios from 'axios';

const BASE = '/backend/api';

const api = axios.create({ baseURL: BASE, withCredentials: true });

api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('token');
  if (token && token !== 'undefined' && token !== 'null') {
    // Ensure we are using the correct header format
    cfg.headers = cfg.headers || {};
    cfg.headers['Authorization'] = `Bearer ${token}`;
    
    // Always log in production if there is an issue, but for now let's just log it
    // console.log(`[API Request] ${cfg.method?.toUpperCase()} ${cfg.url} - Token attached (${token.length} chars)`);
  } else {
    // console.warn(`[API Request] ${cfg.method?.toUpperCase()} ${cfg.url} - No token found`);
  }
  return cfg;
}, err => {
  console.error('[API Request Error]', err);
  return Promise.reject(err);
});

api.interceptors.response.use(
  res => {
    return res;
  },
  err => {
    const status = err.response?.status;
    const url = err.config?.url || '';

    // Only handle 401 if it's not the login request itself
    if (status === 401 && !url.includes('action=login') && !url.includes('action=register')) {
      console.warn('[API 401] Unauthorized access to:', url);
      
      // Check if we just logged in (within the last 5 seconds) to avoid race conditions
      const lastLogin = parseInt(localStorage.getItem('last_login_time') || '0');
      const now = Date.now();
      if (now - lastLogin < 5000) {
        console.warn('[API 401] Ignoring 401 right after login to avoid race condition');
        return Promise.reject(err);
      }

      // CRITICAL: We no longer auto-logout on every 401.
      // Background polling (calls, weighments, conversations) should NEVER force a logout.
      // Only the explicit "verify" session call from AuthContext should be allowed to force logout.
      
      const isSensitiveRequest = url.includes('action=verify') || 
                                 url.includes('action=update_profile') || 
                                 url.includes('action=change_password');

      if (isSensitiveRequest) {
        console.error('[API 401] Sensitive request failed. Preparing logout.');
        
        // Even for sensitive requests, we check if the token still exists.
        // If it was removed manually, then we logout.
        if (!localStorage.getItem('token')) {
          window.dispatchEvent(new CustomEvent('app:logout'));
        }
      } else {
        // For background polling, we just log it and reject the promise.
        // The component handling the poll will just fail this cycle and try again later.
        console.warn('[API 401] Background request failed. Ignoring to prevent auto-logout.');
      }
    }
    
    return Promise.reject(err);
  }
);


export const authAPI = {
  register:       (data) => api.post('/auth.php?action=register', data),
  login:          (data) => api.post('/auth.php?action=login', data),
  logout:         ()     => api.post('/auth.php?action=logout'),
  verify:         ()     => api.get('/auth.php?action=verify'),
  updateProfile: (data) => {
    const fd = new FormData();
    Object.entries(data).forEach(([k, v]) => {
      if (v !== undefined && v !== null) {
        fd.append(k, v);
      }
    });
    return api.post('/auth.php?action=update_profile', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  changePassword: (data) => api.post('/auth.php?action=change_password', data),
  forgotPassword: (phone) => api.post('/auth.php?action=forgot_password', { phone }),
  resetPassword:  (data)  => api.post('/auth.php?action=reset_password', data),
  updatePrivacy:  (data)  => api.post('/auth.php?action=update_privacy', data),
  getPrivacy:     ()      => api.get('/auth.php?action=get_privacy'),
};

export const usersAPI = {
  search:     (q)         => api.get(`/users.php?action=search&q=${encodeURIComponent(q)}`),
  getById:    (id)        => api.get(`/users.php?action=get&id=${id}`),
  contacts:   ()          => api.get('/users.php?action=contacts'),
  addContact: (id)        => api.post('/users.php?action=add_contact', { contact_id: id }),
  block:      (id, block) => api.post('/users.php?action=block', { contact_id: id, block }),
  heartbeat:  ()          => api.post('/users.php?action=heartbeat'),
  offline:    ()          => api.post('/users.php?action=offline'),
};

export const convsAPI = {
  list:        (archived = 0) => api.get(`/conversations.php?action=list&archived=${archived}`),
  direct:      (uid)          => api.post('/conversations.php?action=direct', { user_id: uid }),
  createGroup: (data)         => api.post('/conversations.php?action=create_group', data),
  info:        (id)           => api.get(`/conversations.php?action=info&id=${id}`),
  addMembers:  (data)         => api.post('/conversations.php?action=add_members', data),
  leave:       (id)           => api.post('/conversations.php?action=leave', { conversation_id: id }),
  archive:     (id, v)        => api.post('/conversations.php?action=archive', { conversation_id: id, archive: v }),
  mute:        (id, v)        => api.post('/conversations.php?action=mute',    { conversation_id: id, mute: v }),
  markRead:    (id)           => api.post('/conversations.php?action=mark_read', { conversation_id: id }),
};

export const msgsAPI = {
  list:       (convId, before)   => api.get(`/messages.php?action=list&conversation_id=${convId}${before ? '&before=' + before : ''}`),
  send:       (data)             => {
    const fd = new FormData();
    Object.entries(data).forEach(([k, v]) => v !== undefined && v !== null && fd.append(k, v));
    return api.post('/messages.php?action=send', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  delete:     (id)               => api.post('/messages.php?action=delete',  { message_id: id }),
  edit:       (id, c)            => api.post('/messages.php?action=edit',    { message_id: id, content: c }),
  react:      (id, e)            => api.post('/messages.php?action=react',   { message_id: id, emoji: e }),
  unreact:    (id)               => api.post('/messages.php?action=unreact', { message_id: id }),
  forward:    (msgId, convIds)   => api.post('/messages.php?action=forward', { message_id: msgId, conversation_ids: convIds }),
  poll:       (convId, afterId, updatedSince) =>
    api.get(`/messages.php?action=poll&conversation_id=${convId}&after_id=${afterId}${updatedSince ? '&updated_since=' + encodeURIComponent(updatedSince) : ''}`),
  readStatus: (convId, afterId) =>
    api.get(`/messages.php?action=read_status&conversation_id=${convId}&after_id=${afterId}`),
};

export const storiesAPI = {
  list:   ()     => api.get('/stories.php?action=list'),
  post:   (data) => {
    const fd = new FormData();
    Object.entries(data).forEach(([k, v]) => v !== undefined && fd.append(k, v));
    return api.post('/stories.php?action=post', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  view:   (id)   => api.post('/stories.php?action=view', { story_id: id }),
  delete: (id)   => api.post('/stories.php?action=delete', { story_id: id }),
};

export const callsAPI = {
  initiate: (convId, type)             => api.post('/calls.php?action=initiate', { conversation_id: convId, type }),
  end:      (callId, status, duration) => api.post('/calls.php?action=end', { call_id: callId, status, duration }),
  accept:   (callId)                   => api.post('/calls.php?action=accept',  { call_id: callId }),
  decline:  (callId)                   => api.post('/calls.php?action=decline', { call_id: callId }),
  status:   (callId)                   => api.get(`/calls.php?action=status&call_id=${callId}`),
  poll:     ()                         => api.get('/calls.php?action=poll'),
  history:  ()                         => api.get('/calls.php?action=history'),
  signal:   (callId, signalType, payload) =>
    api.post('/calls.php?action=signal', { call_id: callId, signal_type: signalType, payload }),
  pollSignals: (callId, afterId = 0) =>
    api.get(`/calls.php?action=signals&call_id=${callId}&after_id=${afterId}`),
};

// Customer-facing bills (the merchant invoices the user has received)
export const billsAPI = {
  list:     (status, offset = 0) =>
    api.get(`/bills.php?action=list${status ? '&status=' + status : ''}&offset=${offset}`),
  get:      (id)   => api.get(`/bills.php?action=get&id=${id}`),
  markPaid: (id)   => api.post('/bills.php?action=mark_paid', { bill_id: id }),
};

// ─── Weighment Integration API ─────────────────────────────────────────────
// All calls below use the standard NChat Bearer session token.
// The 'create' endpoint (device-facing) uses X-Weighment-Token instead —
// call it from your weighbridge/ERP system, not from this frontend.
export const weighmentAPI = {
  // Weighment records
  list:        (params = {}) => {
    const qs = new URLSearchParams({ action: 'list', ...params }).toString();
    return api.get(`/weighment.php?${qs}`);
  },
  get:         (id)          => api.get(`/weighment.php?action=get&id=${id}`),
  delete:      (weighmentId) => api.post('/weighment.php?action=delete', { weighment_id: weighmentId }),

  // Weighment-bills
  billList:    (params = {}) => {
    const qs = new URLSearchParams({ action: 'bill_list', ...params }).toString();
    return api.get(`/weighment.php?${qs}`);
  },
  billGet:     (id)          => api.get(`/weighment.php?action=bill_get&id=${id}`),
  billDelete:  (billId)      => api.post('/weighment.php?action=bill_delete',  { bill_id: billId }),
  markPaid:    (billId)      => api.post('/weighment.php?action=mark_paid',    { bill_id: billId }),

  // Real-time polling
  poll:        (afterId = 0) => api.get(`/weighment.php?action=poll&after_id=${afterId}`),

  // Device token management
  tokenCreate: (data)        => api.post('/weighment.php?action=token_create', data),
  tokenList:   ()            => api.get('/weighment.php?action=token_list'),
  tokenDelete: (tokenId)     => api.post('/weighment.php?action=token_delete', { token_id: tokenId }),
};

// Admin partner management (requires user.is_admin)
export const adminAPI = {
  listPartners:   (status)             => api.get('/admin/partners.php' + (status ? `?status=${status}` : '')),
  getPartner:     (id)                 => api.get(`/admin/partners.php?id=${id}`),
  createPartner:  (data)               => api.post('/admin/partners.php?action=create', data),
  updatePartner:  (id, data)           => api.post(`/admin/partners.php?action=update&id=${id}`, data),
  suspendPartner: (id)                 => api.post(`/admin/partners.php?action=suspend&id=${id}`),
  reactivate:     (id)                 => api.post(`/admin/partners.php?action=reactivate&id=${id}`),
  rotateKeys:     (id)                 => api.post(`/admin/partners.php?action=rotate_keys&id=${id}`),
  deletePartner:  (id)                 => api.post(`/admin/partners.php?action=delete&id=${id}`),
};


export const nearbyAPI = {
  list: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== ''))
    ).toString();
    return api.get('/nearby_business.php' + (qs ? '?' + qs : ''));
  },
};

export default api;
